/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layers,
  Calendar,
  Clock,
  Undo2,
  Redo2,
  Plus,
  Moon,
  Sun,
  LayoutDashboard,
  Users,
  PanelRightClose,
  PanelRightOpen,
  History,
} from 'lucide-react';
import {
  PlanDeSalleProps,
  TablePlan,
  ChangementPlan,
  PorteeChangement,
  ImpactChangement,
  EntreeJournal,
  Zone,
  ModeleTable,
  FormeTable,
} from './types.ts';
import { VueSVGPlan } from './VueSVGPlan.tsx';
import { BarreTemps } from './BarreTemps.tsx';
import { BandeauCapacite } from './BandeauCapacite.tsx';
import { BandeauBrouillon } from './BandeauBrouillon.tsx';
import { AlerteLiberer } from './AlerteLiberer.tsx';
import { ColonneReservations } from './ColonneReservations.tsx';
import { MenuActionsTable } from './MenuActionsTable.tsx';
import { ModalPorteeEtImpact } from './ModalPorteeEtImpact.tsx';
import { BibliothequeModeles } from './BibliothequeModeles.tsx';
import { JournalChangements } from './JournalChangements.tsx';
import { BandeauDernierChangement } from './BandeauDernierChangement.tsx';
import { SqueletteChargement, EcranErreur, EcranPlanVide, BanniereHorsLigne } from './EtatsPlan.tsx';
import { fusionnerTables, separerGroupe, rejouerChangements } from './logiquePure.ts';

export const PlanDeSalle: React.FC<PlanDeSalleProps> = ({
  zones,
  tables,
  modeles,
  decorations,
  reservations,
  heureAffichee,
  service,
  permissions,
  etat,
  onDemanderImpact,
  onAppliquer,
  onAttribuer,
  onAnnulerDernierChangement,
  onTerminerReservation,
  onProlonger,
  onChangementHeure,
  onCreerTableDepuisModele,
  theme = 'clair',
  onBasculerTheme,
  heureReelle,
  journalInitial,
  dernierChangement,
}) => {
  // Zone active
  const zonesTriees = useMemo(() => [...zones].sort((a, b) => a.ordre - b.ordre), [zones]);
  const [zoneActiveId, setZoneActiveId] = useState<string>(zonesTriees[0]?.id || '');

  const zoneActive = useMemo(
    () => zones.find((z) => z.id === zoneActiveId) || zonesTriees[0] || { id: '', nom: '', compteAPart: false, ordre: 1 },
    [zones, zoneActiveId, zonesTriees]
  );

  // État local des tables pour le mode brouillon
  const [tablesLocales, setTablesLocales] = useState<TablePlan[]>(tables);
  const [pileChangements, setPileChangements] = useState<ChangementPlan[]>([]);
  const [pileRefaire, setPileRefaire] = useState<ChangementPlan[]>([]);
  const [journal, setJournal] = useState<EntreeJournal[]>([]);
  // Le journal local suit le brouillon : annuler/refaire déplacent l'entrée, appliquer ou réinitialiser le vide (les changements appliqués sont dans le journal du serveur).
  const [journalRefaire, setJournalRefaire] = useState<EntreeJournal[]>([]);

  // Synchronisation avec les tables parentes si aucun brouillon n'est en cours
  useEffect(() => {
    if (pileChangements.length === 0) {
      setTablesLocales(tables);
    }
  }, [tables, pileChangements.length]);

  // Sélections actives
  const [tableSelectionneeId, setTableSelectionneeId] = useState<string>('');
  const [tablesSelectionneesMulti, setTablesSelectionneesMulti] = useState<string[]>([]);
  const [reservationSelectionneeId, setReservationSelectionneeId] = useState<string>('');

  // Modales et tiroirs
  const [menuTableOuvert, setMenuTableOuvert] = useState<boolean>(false);
  const [modalImpactOuverte, setModalImpactOuverte] = useState<boolean>(false);
  const [impactCourant, setImpactCourant] = useState<ImpactChangement | null>(null);
  const [biblioModelesOuverte, setBiblioModelesOuverte] = useState<boolean>(false);
  const [journalOuvert, setJournalOuvert] = useState<boolean>(false);

  // Vue responsive
  const [panneauReservationsOuvert, setPanneauReservationsOuvert] = useState<boolean>(true);
  const [ongletMobile, setOngletMobile] = useState<'plan' | 'reservations'>('plan');
  const [estMobile, setEstMobile] = useState<boolean>(false);

  useEffect(() => {
    const verifierTaille = () => {
      setEstMobile(window.innerWidth < 640);
    };
    verifierTaille();
    window.addEventListener('resize', verifierTaille);
    return () => window.removeEventListener('resize', verifierTaille);
  }, []);

  // Tables de la zone active
  const tablesZone = useMemo(
    () => tablesLocales.filter((t) => t.zoneId === zoneActive.id),
    [tablesLocales, zoneActive.id]
  );

  // Décorations de la zone active
  const decorationsZone = useMemo(
    () => decorations.filter((d) => d.zoneId === zoneActive.id),
    [decorations, zoneActive.id]
  );

  // Table sélectionnée
  const tableSelectionnee = useMemo(
    () => tablesLocales.find((t) => t.id === tableSelectionneeId),
    [tablesLocales, tableSelectionneeId]
  );

  // Fonction d'enregistrement d'une modification en brouillon
  const enregistrerChangement = useCallback(
    (changement: ChangementPlan, descriptionTexte: string) => {
      setPileChangements((prev) => [...prev, changement]);
      setPileRefaire([]); // Réinitialise la pile refaire
      setJournalRefaire([]);

      const nouvEntree: EntreeJournal = {
        id: `log_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        timestamp: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        auteur: permissions.modifierPlan ? 'Direction' : 'Utilisateur',
        description: descriptionTexte,
        changement,
      };
      setJournal((j) => [nouvEntree, ...j]);
    },
    [permissions.modifierPlan]
  );

  // Déplacement d'une table
  const gererDeplacementTable = useCallback(
    (tableId: string, posX: number, posY: number) => {
      setTablesLocales((anciennes) =>
        anciennes.map((t) => (t.id === tableId ? { ...t, posX, posY } : t))
      );
      const t = tablesLocales.find((x) => x.id === tableId);
      enregistrerChangement(
        { type: 'deplacer', tableId, posX, posY },
        `Déplacement table ${t?.nom || tableId}`
      );
    },
    [enregistrerChangement, tablesLocales]
  );

  // Rotation d'une table
  const gererRotationTable = useCallback(
    (tableId: string, rotation: number) => {
      setTablesLocales((anciennes) =>
        anciennes.map((t) => (t.id === tableId ? { ...t, rotation } : t))
      );
      const t = tablesLocales.find((x) => x.id === tableId);
      enregistrerChangement(
        { type: 'pivoter', tableId, rotation },
        `Rotation table ${t?.nom || tableId} à ${rotation}°`
      );
    },
    [enregistrerChangement, tablesLocales]
  );

  // Changement de capacité
  const gererChangementCapacite = useCallback(
    (tableId: string, capaciteMax: number) => {
      setTablesLocales((anciennes) =>
        anciennes.map((t) => (t.id === tableId ? { ...t, capaciteMax } : t))
      );
      const t = tablesLocales.find((x) => x.id === tableId);
      enregistrerChangement(
        { type: 'capacite', tableId, capaciteMax },
        `Table ${t?.nom || tableId} : capacité passée à ${capaciteMax} couverts`
      );
    },
    [enregistrerChangement, tablesLocales]
  );

  // Changement de forme
  const gererChangementForme = useCallback(
    (tableId: string, forme: FormeTable) => {
      setTablesLocales((anciennes) =>
        anciennes.map((t) => (t.id === tableId ? { ...t, forme } : t))
      );
      const t = tablesLocales.find((x) => x.id === tableId);
      enregistrerChangement(
        { type: 'forme', tableId, forme },
        `Table ${t?.nom || tableId} : forme changée en ${forme}`
      );
    },
    [enregistrerChangement, tablesLocales]
  );

  // Fusionner tables
  const gererFusion = useCallback(
    (tableIds: string[], capacite?: number) => {
      const { tablesModifiees, nouvelleCapacite } = fusionnerTables(tablesLocales, tableIds, capacite);
      setTablesLocales(tablesModifiees);
      enregistrerChangement(
        { type: 'fusionner', tableIds, nouvelleCapacite },
        `Fusion de ${tableIds.length} tables (${nouvelleCapacite} couverts combinés)`
      );
      setTablesSelectionneesMulti([]);
    },
    [enregistrerChangement, tablesLocales]
  );

  // Séparer groupe
  const gererSeparation = useCallback(
    (groupeId: string) => {
      const tablesRestaurees = separerGroupe(tablesLocales, groupeId);
      setTablesLocales(tablesRestaurees);
      enregistrerChangement({ type: 'separer', groupeId }, `Séparation du groupe de tables`);
    },
    [enregistrerChangement, tablesLocales]
  );

  // Bloquer / débloquer table
  const gererBlocage = useCallback(
    (tableId: string, bloquee: boolean, motif?: string) => {
      setTablesLocales((anciennes) =>
        anciennes.map((t) => (t.id === tableId ? { ...t, bloquee, motifBlocage: motif } : t))
      );
      const t = tablesLocales.find((x) => x.id === tableId);
      enregistrerChangement(
        { type: 'bloquer', tableId, bloquee, motif },
        bloquee
          ? `Blocage table ${t?.nom || tableId} (${motif || 'maintenance'})`
          : `Déblocage table ${t?.nom || tableId}`
      );
    },
    [enregistrerChangement, tablesLocales]
  );

  // Renommer table
  const gererRenommage = useCallback(
    (tableId: string, nom: string) => {
      setTablesLocales((anciennes) =>
        anciennes.map((t) => (t.id === tableId ? { ...t, nom } : t))
      );
      enregistrerChangement({ type: 'renommer', tableId, nom }, `Table renommée en « ${nom} »`);
    },
    [enregistrerChangement]
  );

  // Dupliquer table
  const gererDuplication = useCallback(
    (tableId: string) => {
      const source = tablesLocales.find((t) => t.id === tableId);
      if (!source) return;

      const nouvId = `table_${Date.now()}`;
      const nouvelleTable: TablePlan = {
        ...source,
        id: nouvId,
        nom: `${source.nom}+`,
        posX: source.posX + 30,
        posY: source.posY + 30,
        groupeId: undefined,
      };

      setTablesLocales((anciennes) => [...anciennes, nouvelleTable]);
      enregistrerChangement(
        { type: 'creer', table: nouvelleTable },
        `Duplication de la table ${source.nom} -> ${nouvelleTable.nom}`
      );
      setTableSelectionneeId(nouvId);
    },
    [enregistrerChangement, tablesLocales]
  );

  // Supprimer table
  const gererSuppression = useCallback(
    (tableId: string) => {
      const t = tablesLocales.find((x) => x.id === tableId);
      setTablesLocales((anciennes) => anciennes.filter((x) => x.id !== tableId));
      enregistrerChangement(
        { type: 'supprimer', tableId },
        `Suppression de la table ${t?.nom || tableId}`
      );
      setTableSelectionneeId('');
    },
    [enregistrerChangement, tablesLocales]
  );

  // Ajouter table depuis modèle
  const gererAjoutDepuisModele = useCallback(
    (modele: ModeleTable) => {
      const nouvId = `table_${Date.now()}`;
      const nouvelleTable: TablePlan = {
        id: nouvId,
        zoneId: zoneActive.id,
        nom: `${modele.nomCourt}${tablesZone.length + 1}`,
        capaciteMin: modele.capaciteMin,
        capaciteMax: modele.capaciteMax,
        forme: modele.forme,
        posX: 100,
        posY: 100,
        rotation: 0,
        largeur: modele.largeur,
        hauteur: modele.hauteur,
        bloquee: false,
        modeleId: modele.id,
      };

      setTablesLocales((anciennes) => [...anciennes, nouvelleTable]);
      enregistrerChangement(
        { type: 'creer', table: nouvelleTable },
        `Création table ${nouvelleTable.nom} (modèle ${modele.nomCourt})`
      );
      setTableSelectionneeId(nouvId);
      setBiblioModelesOuverte(false);
    },
    [enregistrerChangement, tablesZone.length, zoneActive.id]
  );

  // Annuler le dernier changement DU BROUILLON (Ctrl/Cmd+Z, bouton du bandeau) : purement local. Les tables sont recalculées en rejouant le reste du brouillon.
  // Ne touche JAMAIS aux changements déjà appliqués côté serveur (ceux-là s'annulent depuis le journal).
  const annulerDernier = useCallback(() => {
    if (pileChangements.length === 0) return;
    const dernier = pileChangements[pileChangements.length - 1];
    const reste = pileChangements.slice(0, -1);
    setPileChangements(reste);
    setPileRefaire((prev) => [dernier, ...prev]);
    if (journal[0]) setJournalRefaire((r) => [journal[0], ...r]);
    setJournal(journal.slice(1));
    setTablesLocales(rejouerChangements(tables, reste));
  }, [pileChangements, tables, journal]);

  // Rétablir un changement du brouillon (Ctrl/Cmd+Maj+Z)
  const refaireChangement = useCallback(() => {
    if (pileRefaire.length === 0) return;
    const aRefaire = pileRefaire[0];
    const nouvelle = [...pileChangements, aRefaire];
    setPileRefaire((prev) => prev.slice(1));
    if (journalRefaire[0]) setJournal([journalRefaire[0], ...journal]);
    setJournalRefaire(journalRefaire.slice(1));
    setPileChangements(nouvelle);
    setTablesLocales(rejouerChangements(tables, nouvelle));
  }, [pileRefaire, pileChangements, tables, journal, journalRefaire]);

  // Raccourcis clavier globaux Ctrl+Z / Cmd+Z et Ctrl+Maj+Z
  useEffect(() => {
    const gererRaccourcis = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        // Dans un champ de saisie (recherche, nom de table…), Ctrl+Z annule la saisie, pas le plan.
        const cible = e.target as HTMLElement | null;
        if (cible && (cible.tagName === 'INPUT' || cible.tagName === 'TEXTAREA' || cible.tagName === 'SELECT' || cible.isContentEditable)) return;
        if (e.shiftKey) {
          e.preventDefault();
          refaireChangement();
        } else {
          e.preventDefault();
          annulerDernier();
        }
      }
    };
    window.addEventListener('keydown', gererRaccourcis);
    return () => window.removeEventListener('keydown', gererRaccourcis);
  }, [annulerDernier, refaireChangement]);

  // La fenêtre s'ouvre tout de suite : l'effet sur les réservations dépend de la portée choisie, il est donc demandé au serveur à chaque choix de portée
  // (et seulement alors). Aucune estimation locale : si le serveur refuse, son message s'affiche dans la fenêtre.
  const ouvrirModalApplication = () => {
    setImpactCourant({ reservationsTouchees: [], capaciteParTaille: {}, avertissements: [] });
    setModalImpactOuverte(true);
  };

  // Validation finale dans la fenêtre d'application : une erreur (droit, conflit, refus) remonte à la fenêtre, qui reste ouverte.
  const confirmerApplication = async (
    portee: PorteeChangement,
    decisions: Record<string, 'replacer_auto' | 'garder' | 'contacter'>
  ) => {
    await onAppliquer(pileChangements, portee, decisions);
    setPileChangements([]);
    setPileRefaire([]);
    setJournal([]);
    setJournalRefaire([]);
    setModalImpactOuverte(false);
  };

  // Sélection d'une table
  const gererSelectionTable = (tableId: string, estMulti: boolean = false) => {
    if (estMulti) {
      setTablesSelectionneesMulti((prev) =>
        prev.includes(tableId) ? prev.filter((id) => id !== tableId) : [...prev, tableId]
      );
      setTableSelectionneeId(tableId);
    } else {
      setTableSelectionneeId(tableId);
      setTablesSelectionneesMulti([tableId]);
    }
  };

  // Affichage selon l'état de l'écran
  if (etat === 'chargement') return <SqueletteChargement />;
  if (etat === 'erreur') return <EcranErreur onReessayer={() => window.location.reload()} />;
  if (etat === 'vide') {
    return (
      <EcranPlanVide
        onDemarrerDepuisFormats={() => setBiblioModelesOuverte(true)}
        onDemarrerDepuisModele={() => {
          setTablesLocales(tables);
        }}
      />
    );
  }

  return (
    <div className="plan-de-salle flex flex-col h-full w-full bg-surface-app text-content overflow-hidden font-sans select-none">
      {/* 1. Bannière hors ligne le cas échéant */}
      {etat === 'hors_ligne' && <BanniereHorsLigne />}

      {/* 2. Barre d'en-tête : Onglets des zones, formats, thème */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-3 sm:px-4 py-2 flex items-center justify-between gap-2 text-xs select-none">
        {/* Onglets des zones (Salle, Terrasse, Salon privé...) */}
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none py-0.5">
          {zonesTriees.map((z) => {
            const estActive = z.id === zoneActive.id;
            return (
              <button
                key={z.id}
                type="button"
                onClick={() => {
                  setZoneActiveId(z.id);
                  setTableSelectionneeId('');
                }}
                aria-current={estActive ? 'page' : undefined}
                className={`px-3 py-1.5 rounded-lg font-bold text-xs flex items-center gap-1.5 cursor-pointer min-h-[38px] transition-all shrink-0 ${
                  estActive
                    ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-xs'
                    : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'
                }`}
              >
                <span>{z.nom}</span>
                {z.compteAPart && (
                  <span className="w-2 h-2 rounded-full bg-purple-500" title="Zone comptée à part" />
                )}
              </button>
            );
          })}
        </div>

        {/* Boutons d'outils secondaires : Formats, Journal, Thème */}
        <div className="flex items-center gap-1.5 shrink-0">
          {permissions.modifierPlan && (
            <button
              type="button"
              onClick={() => setBiblioModelesOuverte(true)}
              aria-label="Ouvrir la bibliothèque de formats"
              className="px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 font-semibold text-xs flex items-center gap-1.5 cursor-pointer min-h-[38px]"
            >
              <Plus className="w-4 h-4 text-emerald-600" />
              <span className="hidden md:inline">Formats de table</span>
            </button>
          )}

          {journal.length > 0 && (
            <button
              type="button"
              onClick={() => setJournalOuvert(true)}
              aria-label="Voir le journal des changements"
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 min-h-[38px] min-w-[38px] flex items-center justify-center cursor-pointer"
              title="Journal des changements"
            >
              <History className="w-4 h-4" />
            </button>
          )}

          {onBasculerTheme && (
            <button
              type="button"
              onClick={onBasculerTheme}
              aria-label={`Passer en thème ${theme === 'sombre' ? 'clair' : 'sombre'}`}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 min-h-[38px] min-w-[38px] flex items-center justify-center cursor-pointer"
            >
              {theme === 'sombre' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
            </button>
          )}

          {/* Toggle colonne réservations sur grand écran */}
          <button
            type="button"
            onClick={() => setPanneauReservationsOuvert((p) => !p)}
            aria-label={panneauReservationsOuvert ? 'Masquer réservations' : 'Afficher réservations'}
            className="hidden sm:flex p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 min-h-[38px] min-w-[38px] items-center justify-center cursor-pointer"
            title="Panneau réservations"
          >
            {panneauReservationsOuvert ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* 3. Barre de temps (15 min et bouton Maintenant) */}
      <BarreTemps
        heureAffichee={heureAffichee}
        heureReelle={heureReelle || heureAffichee}
        service={service}
        onChangementHeure={(h) => onChangementHeure?.(h)}
        onRevenirMaintenant={() => onChangementHeure?.(heureReelle || heureAffichee)}
      />

      {/* 4. Bandeau de capacité par taille de groupe recalculé en direct */}
      <BandeauCapacite
        tables={tablesZone}
        reservations={reservations}
        heureAffichee={heureAffichee}
        zoneActive={zoneActive}
        estEnBrouillon={pileChangements.length > 0}
      />

      {/* 4 bis. Dernier changement appliqué, annulable en un clic */}
      {dernierChangement && (
        <BandeauDernierChangement
          description={dernierChangement.description}
          portee={dernierChangement.portee}
          peutAnnuler={permissions.modifierPlan}
          brouillonEnCours={pileChangements.length > 0}
          onAnnuler={onAnnulerDernierChangement}
        />
      )}

      {/* 5. Alerte si une table dépasse sa durée prévue (À LIBÉRER) */}
      <AlerteLiberer
        tables={tablesLocales}
        reservations={reservations}
        heureAffichee={heureAffichee}
        onProlonger={onProlonger}
        onTerminerReservation={onTerminerReservation}
        onSelectionnerTable={(tid) => {
          setTableSelectionneeId(tid);
          const t = tablesLocales.find((x) => x.id === tid);
          if (t && t.zoneId !== zoneActive.id) {
            setZoneActiveId(t.zoneId);
          }
        }}
      />

      {/* 6. Bandeau de brouillon en direct */}
      <BandeauBrouillon
        changements={pileChangements}
        peutDefaire={pileChangements.length > 0}
        peutRefaire={pileRefaire.length > 0}
        onAnnulerDernier={annulerDernier}
        onRefaire={refaireChangement}
        onOuvrirModalApplication={ouvrirModalApplication}
        onReinitialiserBrouillon={() => {
          setTablesLocales(tables);
          setPileChangements([]);
          setPileRefaire([]);
          setJournal([]);
          setJournalRefaire([]);
        }}
        onOuvrirJournal={() => setJournalOuvert(true)}
      />

      {/* 7. Onglets pour mobile (390px) pour alterner entre Plan et Réservations */}
      {estMobile && (
        <div className="flex border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-xs font-bold">
          <button
            type="button"
            onClick={() => setOngletMobile('plan')}
            className={`flex-1 py-2.5 text-center min-h-[44px] cursor-pointer ${
              ongletMobile === 'plan'
                ? 'border-b-2 border-slate-950 dark:border-slate-100 text-slate-950 dark:text-slate-100 bg-white dark:bg-slate-950'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            Plan de salle
          </button>
          <button
            type="button"
            onClick={() => setOngletMobile('reservations')}
            className={`flex-1 py-2.5 text-center min-h-[44px] cursor-pointer ${
              ongletMobile === 'reservations'
                ? 'border-b-2 border-slate-950 dark:border-slate-100 text-slate-950 dark:text-slate-100 bg-white dark:bg-slate-950'
                : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            Réservations (
            {reservations.filter((r) => r.tableIds.length === 0).length} à placer)
          </button>
        </div>
      )}

      {/* 8. Conteneur principal (Plan SVG + Colonne des réservations) */}
      <main className="flex-1 flex overflow-hidden relative">
        {/* Plan SVG */}
        <div
          className={`flex-1 relative flex flex-col h-full ${
            estMobile && ongletMobile === 'reservations' ? 'hidden' : 'flex'
          }`}
        >
          <VueSVGPlan
            tables={tablesZone}
            decorations={decorationsZone}
            reservations={reservations}
            heureAffichee={heureAffichee}
            tableSelectionneeId={tableSelectionneeId}
            tablesSelectionneesMulti={tablesSelectionneesMulti}
            reservationMiseEnEvidenceId={reservationSelectionneeId}
            lectureSeule={!permissions.modifierPlan}
            theme={theme}
            onSelectionnerTable={gererSelectionTable}
            onDeplacerTable={gererDeplacementTable}
            onPivoterTable={gererRotationTable}
            onOuvrirMenuTable={(id) => {
              setTableSelectionneeId(id);
              setMenuTableOuvert(true);
            }}
          />

          {/* Barre contextuelle d'actions de table (en bas ou tiroir) */}
          {tableSelectionnee && (
            <div
              className={`${
                estMobile
                  ? 'fixed inset-x-0 bottom-0 z-40'
                  : 'absolute bottom-4 left-4 z-30'
              }`}
            >
              <MenuActionsTable
                table={tableSelectionnee}
                toutesTables={tablesLocales}
                modeles={modeles}
                permissions={permissions}
                tablesSelectionneesMulti={tablesSelectionneesMulti}
                estMobile={estMobile}
                onFermer={() => {
                  setMenuTableOuvert(false);
                  setTableSelectionneeId('');
                }}
                onChangerCapacite={gererChangementCapacite}
                onChangerForme={gererChangementForme}
                onPivoter={(id, delta) =>
                  gererRotationTable(id, ((tableSelectionnee.rotation + delta) % 360 + 360) % 360)
                }
                onBloquer={gererBlocage}
                onFusionner={gererFusion}
                onSeparer={gererSeparation}
                onDupliquer={gererDuplication}
                onRenommer={gererRenommage}
                onSupprimer={gererSuppression}
              />
            </div>
          )}
        </div>

        {/* Colonne des réservations */}
        {(!estMobile && panneauReservationsOuvert) || (estMobile && ongletMobile === 'reservations') ? (
          <div className="h-full">
            <ColonneReservations
              reservations={reservations}
              tables={tablesLocales}
              heureAffichee={heureAffichee}
              permissions={permissions}
              reservationSelectionneeId={reservationSelectionneeId}
              tableSelectionneeId={tableSelectionneeId}
              onSelectionnerReservation={(rid) => {
                setReservationSelectionneeId(rid);
                // Si la réservation est déjà sur une table, sélectionner sa table et basculer sur sa zone
                const r = reservations.find((x) => x.id === rid);
                if (r && r.tableIds.length > 0) {
                  const premiereTable = tablesLocales.find((t) => t.id === r.tableIds[0]);
                  if (premiereTable) {
                    setTableSelectionneeId(premiereTable.id);
                    if (premiereTable.zoneId !== zoneActive.id) {
                      setZoneActiveId(premiereTable.zoneId);
                    }
                  }
                }
              }}
              onAttribuer={onAttribuer}
            />
          </div>
        ) : null}
      </main>

      {/* 9. Modale de portée et aperçu d'impact avant application */}
      {modalImpactOuverte && impactCourant && (
        <ModalPorteeEtImpact
          changements={pileChangements}
          impact={impactCourant}
          reservations={reservations}
          onFermer={() => setModalImpactOuverte(false)}
          onDemanderImpact={(portee) => onDemanderImpact(pileChangements, portee)}
          onConfirmerApplication={confirmerApplication}
        />
      )}

      {/* 10. Bibliothèque de modèles */}
      <BibliothequeModeles
        modeles={modeles}
        estOuverte={biblioModelesOuverte}
        onFermer={() => setBiblioModelesOuverte(false)}
        onAjouterTableDepuisModele={gererAjoutDepuisModele}
      />

      {/* 11. Journal des changements */}
      <JournalChangements
        estOuvert={journalOuvert}
        journal={journal}
        journalServeur={journalInitial}
        onFermer={() => setJournalOuvert(false)}
      />
    </div>
  );
};
